/**
 * Pure terrain-art geometry for the COMBAT.app board (`projects/terrain-tile-art.md` Phase 2,
 * `decisions/0184`). No DOM and no canvas: the painter (Phase 3) turns these shapes into pixels.
 *
 * Ported from the `map-visual-redesign` / `terrain-tile-art` prototypes (`lib.js`, `art.js`), with
 * one change of contract: **every value is a pure function of board coordinates and a bounded
 * neighbourhood**, never of scan order or of the window it was computed for. The board renders
 * as 16×16-cell chunks that repaint alone ({@link CHUNK}), so a chunk's geometry, computed for the
 * chunk plus a halo, must equal the whole board's geometry inside that chunk. `geometry.test.ts`
 * pins that.
 *
 * Coordinates: cells are integer `(x, y)`; pixels are board px at scale 1 (`CELL` px per cell).
 * Scalar fields are sampled on one global lattice, every {@link STEP} px from the board origin,
 * so a sample's value never depends on the window that asked for it.
 */
import { CUSTOM_TILE, terrainPropertiesKey, tileProperties, type EdgeMap, type TerrainCell, type TerrainMap, type TerrainProperties } from "@lancer-vtt/domain";
import { CELL, edgeSegment, edgeTreatment, parseEdgeCells, parseTileKey, type EdgeVisual } from "../combatCells";

/** Field sample pitch in px. `CELL` is a whole number of steps, so cell edges sit on samples. */
export const STEP = 8;
const PER_CELL = CELL / STEP;

/** Chunk side in cells: the unit the board repaints. Even, so cluster blocks never straddle one. */
export const CHUNK = 16;

/**
 * Cells of context painted around a chunk and clipped away. It covers what reaches across a chunk
 * edge: the largest prop plus its cast shadow (a 2×2 slab, about 1.5 cells) and a raised wall's
 * shadow (about 1 cell). Field values need no halo: they read the whole-board mask.
 */
export const CHUNK_HALO = 2;

export type Board = { width: number; height: number };

/** A half-open rectangle of cells: `x0 <= x < x1`, `y0 <= y < y1`. */
export type CellRect = { x0: number; y0: number; x1: number; y1: number };

/** What a field is computed for: a cell rectangle plus a margin in px (a chunk and its halo). */
export type FieldWindow = { rect: CellRect; haloPx?: number };

export function boardRect(board: Board): CellRect {
  return { x0: 0, y0: 0, x1: board.width, y1: board.height };
}

/** Chunk `(cx, cy)`'s cells, clipped to the board. */
export function chunkRect(board: Board, cx: number, cy: number): CellRect {
  return { x0: cx * CHUNK, y0: cy * CHUNK, x1: Math.min(board.width, (cx + 1) * CHUNK), y1: Math.min(board.height, (cy + 1) * CHUNK) };
}

export function growRect(r: CellRect, by: number): CellRect {
  return { x0: r.x0 - by, y0: r.y0 - by, x1: r.x1 + by, y1: r.y1 + by };
}

// ── Coordinate noise ─────────────────────────────────────────────────────────────────────────────

/** A deterministic hash of integer coordinates and a salt, in `[0, 1]`. */
export function rnd(x: number, y: number, k = 0): number {
  let h = (x * 374761393 + y * 668265263 + k * 1442695041) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

function vnoise(x: number, y: number, seed: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const a = rnd(ix, iy, seed);
  const b = rnd(ix + 1, iy, seed);
  const c = rnd(ix, iy + 1, seed);
  const d = rnd(ix + 1, iy + 1, seed);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

/** Fractal value noise in `[0, 1]`. */
export function fbm(x: number, y: number, { seed = 1, octaves = 3 }: { seed?: number; octaves?: number } = {}): number {
  let sum = 0;
  let amp = 0.5;
  let freq = 1;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    sum += amp * vnoise(x * freq, y * freq, seed + o * 17);
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / norm;
}

/** A stable 31-bit seed for a string (a region key), so each region wobbles differently. */
export function stringSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0) % 2147483647;
}

// ── Cell masks and regions ───────────────────────────────────────────────────────────────────────

export type CellMask = { width: number; height: number; cells: Uint8Array };

export function emptyMask(board: Board): CellMask {
  return { width: board.width, height: board.height, cells: new Uint8Array(board.width * board.height) };
}

/** Whether `(x, y)` is set; a cell off the board is not. */
export function maskHas(m: CellMask, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < m.width && y < m.height && m.cells[y * m.width + x] === 1;
}

/** The set cells inside `rect`, row by row. */
export function maskCells(m: CellMask, rect: CellRect): [number, number][] {
  const out: [number, number][] = [];
  for (let y = Math.max(0, rect.y0); y < Math.min(m.height, rect.y1); y++) {
    for (let x = Math.max(0, rect.x0); x < Math.min(m.width, rect.x1); x++) if (m.cells[y * m.width + x] === 1) out.push([x, y]);
  }
  return out;
}

/**
 * The key cells group by into one merged region: a catalogue tile groups by its id; a custom cell
 * by its normalized property set, so two custom cells with the same mechanics merge.
 */
export function regionKey(cell: TerrainCell): string {
  if (cell.tile !== CUSTOM_TILE) return cell.tile;
  return `${CUSTOM_TILE}:${terrainPropertiesKey(cell.properties)}`;
}

/** One merged region: every cell sharing a {@link regionKey}, whether connected or not. */
export type Region = { key: string; cell: TerrainCell; properties: TerrainProperties; mask: CellMask };

/** The terrain map grouped into regions. Cells off the board are dropped. Ordered by key. */
export function groupRegions(terrain: TerrainMap, board: Board): Region[] {
  const byKey = new Map<string, Region>();
  for (const [k, cell] of Object.entries(terrain)) {
    const { x, y } = parseTileKey(k);
    if (x < 0 || y < 0 || x >= board.width || y >= board.height) continue;
    const key = regionKey(cell);
    let region = byKey.get(key);
    if (!region) {
      region = { key, cell, properties: tileProperties(cell), mask: emptyMask(board) };
      byKey.set(key, region);
    }
    region.mask.cells[y * board.width + x] = 1;
  }
  return [...byKey.values()].sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}

// ── Scalar fields ────────────────────────────────────────────────────────────────────────────────

/**
 * A scalar field over a window of the global sample lattice: sample `(i, j)` of `v` sits at px
 * `((i0 + i) * STEP, (j0 + j) * STEP)`. `outside` is the value every sample beyond the board's
 * outer ring holds (and the ring itself), below any threshold a caller contours at, so every
 * outline closes.
 */
export type Field = { i0: number; j0: number; nx: number; ny: number; v: Float64Array; outside: number };

/**
 * The sample window for `rect` grown by `haloPx`, clipped to the board's sample domain: one cell
 * beyond each board edge, whose outermost ring is forced to `outside` so outlines close there.
 */
function fieldWindow(board: Board, { rect, haloPx = 0 }: FieldWindow, outside: number): Field {
  const lo = -PER_CELL;
  const hiX = (board.width + 1) * PER_CELL;
  const hiY = (board.height + 1) * PER_CELL;
  const i0 = Math.max(lo, Math.floor((rect.x0 * CELL - haloPx) / STEP));
  const j0 = Math.max(lo, Math.floor((rect.y0 * CELL - haloPx) / STEP));
  const i1 = Math.min(hiX, Math.ceil((rect.x1 * CELL + haloPx) / STEP));
  const j1 = Math.min(hiY, Math.ceil((rect.y1 * CELL + haloPx) / STEP));
  const nx = Math.max(0, i1 - i0 + 1);
  const ny = Math.max(0, j1 - j0 + 1);
  return { i0, j0, nx, ny, v: new Float64Array(nx * ny).fill(outside), outside };
}

/** Whether global sample `(gi, gj)` is on the board's outer ring (held at `outside`). */
function onBoardRing(board: Board, gi: number, gj: number): boolean {
  return gi <= -PER_CELL || gj <= -PER_CELL || gi >= (board.width + 1) * PER_CELL || gj >= (board.height + 1) * PER_CELL;
}

function erf(x: number): number {
  const t = 1 / (1 + 0.3275911 * Math.abs(x));
  const y = 1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
  return x >= 0 ? y : -y;
}
const phi = (x: number) => 0.5 * (1 + erf(x / Math.SQRT2));

/**
 * The weight cell `cx + d` gives a sample at `cx * CELL + phase * STEP` under a Gaussian of
 * `sigma` px: the Gaussian's mass over the cell's span on one axis. Indexed `[phase][d + r]`.
 */
function gaussWeights(sigma: number, r: number): number[][] {
  const out: number[][] = [];
  for (let p = 0; p < PER_CELL; p++) {
    const row: number[] = [];
    for (let d = -r; d <= r; d++) row.push(phi((p * STEP - d * CELL) / sigma) - phi((p * STEP - (d + 1) * CELL) / sigma));
    out.push(row);
  }
  return out;
}

const mod = (a: number, n: number) => ((a % n) + n) % n;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * The cell mask blurred by a Gaussian of `sigma` px, sampled over `rect` plus `haloPx`. Cells off
 * the board read as the nearest board cell, so a region touching the edge runs off it instead of
 * rounding away. Separable: a row pass, then a column pass, each summing in a fixed cell order, so
 * a sample's value is identical whichever window computes it.
 */
export function coverageField(mask: CellMask, window: FieldWindow, sigma: number): Field {
  const board = { width: mask.width, height: mask.height };
  const F = fieldWindow(board, window, 0);
  const r = Math.ceil((3 * sigma) / CELL) + 1;
  const w = gaussWeights(sigma, r);
  const rows = new Map<number, Float64Array>();
  // Row pass: rowSum[i] = Σ_x wx(i, x) · mask(x, row), for one clamped board row.
  const rowSum = (row: number) => {
    let h = rows.get(row);
    if (h) return h;
    h = new Float64Array(F.nx);
    for (let i = 0; i < F.nx; i++) {
      const gi = F.i0 + i;
      const cx = Math.floor(gi / PER_CELL);
      const wx = w[mod(gi, PER_CELL)]!;
      let s = 0;
      for (let d = -r; d <= r; d++) {
        if (mask.cells[row * mask.width + clamp(cx + d, 0, mask.width - 1)] === 1) s += wx[d + r]!;
      }
      h[i] = s;
    }
    rows.set(row, h);
    return h;
  };
  for (let j = 0; j < F.ny; j++) {
    const gj = F.j0 + j;
    const cy = Math.floor(gj / PER_CELL);
    const wy = w[mod(gj, PER_CELL)]!;
    const hs: Float64Array[] = [];
    for (let d = -r; d <= r; d++) hs.push(rowSum(clamp(cy + d, 0, mask.height - 1)));
    for (let i = 0; i < F.nx; i++) {
      if (onBoardRing(board, F.i0 + i, gj)) continue;
      let s = 0;
      for (let d = 0; d < hs.length; d++) s += wy[d]! * hs[d]![i]!;
      F.v[j * F.nx + i] = s;
    }
  }
  return F;
}

/** Offset a field by fractal noise of amplitude `amp` at feature size `scale` px, so outlines read as organic. */
export function wobbleField(F: Field, board: Board, { amp, scale, seed }: { amp: number; scale: number; seed: number }): Field {
  for (let j = 0; j < F.ny; j++) {
    for (let i = 0; i < F.nx; i++) {
      const gi = F.i0 + i;
      const gj = F.j0 + j;
      if (onBoardRing(board, gi, gj)) continue;
      F.v[j * F.nx + i]! += (fbm((gi * STEP) / scale, (gj * STEP) / scale, { seed }) - 0.5) * 2 * amp;
    }
  }
  return F;
}

/**
 * How a region's outline is shaped: Gaussian blur `sigma` px, noise wobble `amp` at feature size
 * `scale` px, and the half-width `feather` of the alpha fade around the 0.5 level (the painter's
 * business; the outline is always the 0.5 level).
 */
export type RegionParams = { sigma: number; amp: number; scale: number; feather: number };

/** A region's outline field. Its 0.5 contour contains exactly the centres of the mask's cells. */
export function regionField(mask: CellMask, window: FieldWindow, shape: RegionParams & { seed: number }): Field {
  const F = coverageField(mask, window, shape.sigma);
  return shape.amp > 0 ? wobbleField(F, mask, shape) : F;
}

/**
 * Minus the Chebyshev distance (px) from each sample to the nearest set cell's square, floored at
 * `-maxPx` (the search is bounded). `contours(F, -d)` encloses everything within `d` of the mask:
 * at `d = CELL` that is exactly the mask plus its eight-neighbours' centres, the first terrace band.
 */
export function chebyshevField(mask: CellMask, window: FieldWindow, maxPx: number): Field {
  const board = { width: mask.width, height: mask.height };
  const F = fieldWindow(board, window, -(maxPx + CELL));
  const r = Math.ceil(maxPx / CELL) + 1;
  for (let j = 0; j < F.ny; j++) {
    const gj = F.j0 + j;
    const py = gj * STEP;
    const cy = Math.floor(py / CELL);
    for (let i = 0; i < F.nx; i++) {
      const gi = F.i0 + i;
      if (onBoardRing(board, gi, gj)) continue;
      const px = gi * STEP;
      const cx = Math.floor(px / CELL);
      let d = maxPx;
      for (let y = cy - r; y <= cy + r; y++) {
        for (let x = cx - r; x <= cx + r; x++) {
          if (!maskHas(mask, x, y)) continue;
          const dx = Math.max(x * CELL - px, 0, px - (x + 1) * CELL);
          const dy = Math.max(y * CELL - py, 0, py - (y + 1) * CELL);
          d = Math.min(d, Math.max(dx, dy));
        }
      }
      F.v[j * F.nx + i] = -d;
    }
  }
  return F;
}

/** The field's value at board px `(x, y)`, bilinear between samples; `outside` beyond the window. */
export function fieldAt(F: Field, x: number, y: number): number {
  const gx = x / STEP - F.i0;
  const gy = y / STEP - F.j0;
  const i = Math.floor(gx);
  const j = Math.floor(gy);
  if (i < 0 || j < 0 || i + 1 >= F.nx || j + 1 >= F.ny) return F.outside;
  const fx = gx - i;
  const fy = gy - j;
  const v = (a: number, b: number) => F.v[(j + b) * F.nx + i + a]!;
  return (v(0, 0) * (1 - fx) + v(1, 0) * fx) * (1 - fy) + (v(0, 1) * (1 - fx) + v(1, 1) * fx) * fy;
}

// ── Marching squares ─────────────────────────────────────────────────────────────────────────────

export type Point = [number, number];

/** One contour segment inside the marching square whose top-left sample is global `(gi, gj)`. */
export type ContourSegment = { gi: number; gj: number; a: Point; b: Point };

function sampleAt(F: Field, gi: number, gj: number): number {
  const i = gi - F.i0;
  const j = gj - F.j0;
  return i < 0 || j < 0 || i >= F.nx || j >= F.ny ? F.outside : F.v[j * F.nx + i]!;
}

type EdgeRef = { h: boolean; gi: number; gj: number };

/**
 * Walk every marching square of the window plus a virtual ring of `outside` samples around it,
 * handing each pair of crossing edges to `link`. The ring closes every outline without touching a
 * real sample, so squares inside the window see exactly the board's values.
 */
function march(F: Field, t: number, link: (sq: { gi: number; gj: number }, p: EdgeRef, q: EdgeRef) => void): void {
  for (let gj = F.j0 - 1; gj < F.j0 + F.ny; gj++) {
    for (let gi = F.i0 - 1; gi < F.i0 + F.nx; gi++) {
      const va = sampleAt(F, gi, gj);
      const vb = sampleAt(F, gi + 1, gj);
      const vc = sampleAt(F, gi + 1, gj + 1);
      const vd = sampleAt(F, gi, gj + 1);
      const a = va >= t;
      const b = vb >= t;
      const c = vc >= t;
      const d = vd >= t;
      const T: EdgeRef = { h: true, gi, gj };
      const R: EdgeRef = { h: false, gi: gi + 1, gj };
      const B: EdgeRef = { h: true, gi, gj: gj + 1 };
      const L: EdgeRef = { h: false, gi, gj };
      const cut: EdgeRef[] = [];
      if (a !== b) cut.push(T);
      if (b !== c) cut.push(R);
      if (d !== c) cut.push(B);
      if (a !== d) cut.push(L);
      const sq = { gi, gj };
      if (cut.length === 2) link(sq, cut[0]!, cut[1]!);
      else if (cut.length === 4) {
        const centre = (va + vb + vc + vd) / 4 >= t;
        if (a === centre) {
          link(sq, T, R);
          link(sq, B, L);
        } else {
          link(sq, L, T);
          link(sq, R, B);
        }
      }
    }
  }
}

function edgePoint(F: Field, t: number, e: EdgeRef): Point {
  const a = sampleAt(F, e.gi, e.gj);
  const b = e.h ? sampleAt(F, e.gi + 1, e.gj) : sampleAt(F, e.gi, e.gj + 1);
  const f = (t - a) / (b - a);
  return e.h ? [(e.gi + f) * STEP, e.gj * STEP] : [e.gi * STEP, (e.gj + f) * STEP];
}

/** Every contour segment at level `t`, square by square. */
export function contourSegments(F: Field, t: number): ContourSegment[] {
  const out: ContourSegment[] = [];
  march(F, t, (sq, p, q) => out.push({ gi: sq.gi, gj: sq.gj, a: edgePoint(F, t, p), b: edgePoint(F, t, q) }));
  return out;
}

/** The level-`t` contour as closed loops of points (px). Inside is `v >= t`; fill even-odd. */
export function contours(F: Field, t: number): Point[][] {
  // Edge ids are numeric over the window plus its ring: two per sample (horizontal, vertical).
  const w = F.nx + 2;
  const id = (e: EdgeRef) => ((e.gj - F.j0 + 1) * w + (e.gi - F.i0 + 1)) * 2 + (e.h ? 0 : 1);
  const refs = new Map<number, EdgeRef>();
  const adj = new Map<number, number[]>();
  march(F, t, (_sq, p, q) => {
    const ip = id(p);
    const iq = id(q);
    refs.set(ip, p);
    refs.set(iq, q);
    (adj.get(ip) ?? adj.set(ip, []).get(ip)!).push(iq);
    (adj.get(iq) ?? adj.set(iq, []).get(iq)!).push(ip);
  });
  const loops: Point[][] = [];
  const seen = new Set<number>();
  for (const start of adj.keys()) {
    if (seen.has(start)) continue;
    const loop: Point[] = [];
    let prev = -1;
    let cur: number | undefined = start;
    while (cur !== undefined && !seen.has(cur)) {
      seen.add(cur);
      loop.push(edgePoint(F, t, refs.get(cur)!));
      const from: number = prev;
      const next: number | undefined = adj.get(cur)!.find((n) => n !== from && !seen.has(n));
      prev = cur;
      cur = next;
    }
    if (loop.length > 2) loops.push(loop);
  }
  return loops;
}

/** Even-odd point-in-polygon over a set of loops. */
export function insideLoops(loops: Point[][], x: number, y: number): boolean {
  let inside = false;
  for (const L of loops) {
    for (let k = 0, m = L.length - 1; k < L.length; m = k++) {
      const [xk, yk] = L[k]!;
      const [xm, ym] = L[m]!;
      if (yk > y !== ym > y && x < ((xm - xk) * (y - yk)) / (ym - yk) + xk) inside = !inside;
    }
  }
  return inside;
}

// ── Props ────────────────────────────────────────────────────────────────────────────────────────

/** Soft cover: small props kept this far inside their cell, and never larger than it. */
export const SOFT_PROP_INSET = 7;
export const SOFT_PROP_R: readonly [number, number] = [3.5, 6.5];

/** Hard cover: the smallest big prop (a single) is well over twice the largest soft one. */
export const HARD_PROP_MIN_R = 13;

/** A small cover prop, inside the inset of cell `(x, y)`. */
export type SoftProp = { x: number; y: number; cx: number; cy: number; r: number; seed: number };

/** `perCell` small props per set cell of `rect`, positioned by the cell's coordinates. */
export function softProps(mask: CellMask, rect: CellRect, { perCell, salt }: { perCell: number; salt: number }): SoftProp[] {
  const span = CELL - 2 * SOFT_PROP_INSET;
  const out: SoftProp[] = [];
  for (const [x, y] of maskCells(mask, rect)) {
    for (let k = 0; k < perCell; k++) {
      out.push({
        x,
        y,
        cx: x * CELL + SOFT_PROP_INSET + rnd(x, y, salt + k * 3) * span,
        cy: y * CELL + SOFT_PROP_INSET + rnd(x, y, salt + k * 3 + 1) * span,
        r: SOFT_PROP_R[0] + (SOFT_PROP_R[1] - SOFT_PROP_R[0]) * rnd(x, y, salt + k * 3 + 2),
        seed: rnd(x, y, salt + 97 + k) * 2147483647,
      });
    }
  }
  return out;
}

/**
 * A big cover prop over a hard-cover cluster: `slab` over a 2×2 of cover, `pair` over two
 * orthogonal neighbours (rotated along them), `single` over one cell. `cells` are the cells it
 * stands for; every hard-cover cell is in exactly one prop's `cells`.
 */
export type ClusterProp = { kind: "slab" | "pair" | "single"; cells: [number, number][]; cx: number; cy: number; r: number; rot: number; seed: number };

/**
 * The big props for a hard-cover mask over `rect`. Local by construction: the board is cut into
 * 2×2 blocks on an even-aligned lattice, and each block decides from its own four cells and
 * coordinates alone (a full block may become one slab; else up to two pairs inside it; the rest
 * singles). A chunk (even-sized) therefore repaints alone. `clustered: false` gives every cell a
 * single, for a tile whose prop is one object per cell (a tree).
 */
export function clusterPlan(mask: CellMask, rect: CellRect, { salt, clustered = true }: { salt: number; clustered?: boolean }): ClusterProp[] {
  const out: ClusterProp[] = [];
  const bx0 = Math.floor(rect.x0 / 2) * 2;
  const by0 = Math.floor(rect.y0 / 2) * 2;
  for (let by = by0; by < rect.y1; by += 2) {
    for (let bx = bx0; bx < rect.x1; bx += 2) {
      const block: [number, number][] = [
        [bx, by],
        [bx + 1, by],
        [bx, by + 1],
        [bx + 1, by + 1],
      ];
      const free = new Set(block.filter(([x, y]) => maskHas(mask, x, y)).map(([x, y]) => `${x},${y}`));
      if (free.size === 0) continue;
      const has = (x: number, y: number) => free.has(`${x},${y}`);
      if (clustered && free.size === 4 && rnd(bx, by, salt) < 0.55) {
        out.push({
          kind: "slab",
          cells: block,
          cx: (bx + 1) * CELL + (rnd(bx, by, salt + 1) - 0.5) * 8,
          cy: (by + 1) * CELL + (rnd(bx, by, salt + 2) - 0.5) * 8,
          r: 27 + 5 * rnd(bx, by, salt + 3),
          rot: 0,
          seed: rnd(bx, by, salt + 4) * 2147483647,
        });
        continue;
      }
      if (clustered) {
        // The block's four orthogonal pairs, tried in an order its hash picks.
        const pairs: [[number, number], [number, number], boolean][] = [
          [[bx, by], [bx + 1, by], true],
          [[bx, by + 1], [bx + 1, by + 1], true],
          [[bx, by], [bx, by + 1], false],
          [[bx + 1, by], [bx + 1, by + 1], false],
        ];
        const start = Math.floor(rnd(bx, by, salt + 5) * 4);
        for (let k = 0; k < 4; k++) {
          const [p, q, horiz] = pairs[(start + k) % 4]!;
          if (!has(...p) || !has(...q) || rnd(p[0], p[1], salt + 6 + k) >= 0.6) continue;
          free.delete(`${p[0]},${p[1]}`);
          free.delete(`${q[0]},${q[1]}`);
          out.push({
            kind: "pair",
            cells: [p, q],
            cx: ((p[0] + q[0] + 1) / 2) * CELL + (rnd(p[0], p[1], salt + 10) - 0.5) * 6,
            cy: ((p[1] + q[1] + 1) / 2) * CELL + (rnd(p[0], p[1], salt + 11) - 0.5) * 6,
            r: 21 + 4 * rnd(p[0], p[1], salt + 12),
            rot: (horiz ? 0 : Math.PI / 2) + (rnd(p[0], p[1], salt + 13) - 0.5) * 0.5,
            seed: rnd(p[0], p[1], salt + 14) * 2147483647,
          });
        }
      }
      for (const [x, y] of block) {
        if (!has(x, y)) continue;
        out.push({
          kind: "single",
          cells: [[x, y]],
          cx: (x + 0.5) * CELL + (rnd(x, y, salt + 20) - 0.5) * 14,
          cy: (y + 0.5) * CELL + (rnd(x, y, salt + 21) - 0.5) * 14,
          r: HARD_PROP_MIN_R + 5 * rnd(x, y, salt + 22),
          rot: rnd(x, y, salt + 23) * Math.PI,
          seed: rnd(x, y, salt + 24) * 2147483647,
        });
      }
    }
  }
  // Painted top to bottom, so a lower prop overlaps the one behind it.
  return out.sort((a, b) => a.cy - b.cy || a.cx - b.cx);
}

// ── Grid and walls ───────────────────────────────────────────────────────────────────────────────

/** A cell-corner grid cross at lattice point `(x, y)`; `major` every fifth line both ways. */
export type GridCross = { x: number; y: number; major: boolean };

/** The corner crosses of `rect`'s cells (both closing lines included), within the board. */
export function gridCrosses(board: Board, rect: CellRect): GridCross[] {
  const out: GridCross[] = [];
  for (let y = Math.max(0, rect.y0); y <= Math.min(board.height, rect.y1); y++) {
    for (let x = Math.max(0, rect.x0); x <= Math.min(board.width, rect.x1); x++) out.push({ x, y, major: x % 5 === 0 && y % 5 === 0 });
  }
  return out;
}

/** An edge wall's segment in px, with its class (`combatCells.ts`'s `edgeTreatment`). */
export type WallSegment = { key: string; x1: number; y1: number; x2: number; y2: number; visual: EdgeVisual };

/** The walls in `edges` with at least one side in `rect` (all walls when `rect` is omitted), by key. */
export function wallSegments(edges: EdgeMap, rect?: CellRect): WallSegment[] {
  const out: WallSegment[] = [];
  for (const [key, flags] of Object.entries(edges)) {
    const visual = edgeTreatment(flags);
    if (visual === null) continue;
    const [a, b] = parseEdgeCells(key);
    if (rect && ![a, b].some((c) => c.x >= rect.x0 && c.x < rect.x1 && c.y >= rect.y0 && c.y < rect.y1)) continue;
    const seg = edgeSegment(a, b);
    if (seg) out.push({ key, ...seg, visual });
  }
  return out.sort((p, q) => (p.key < q.key ? -1 : p.key > q.key ? 1 : 0));
}
